import * as Sentry from '@sentry/react'

import Client from '../../lib/client'
import hasAnalyticsConsent from '../hasAnalyticsConsent'
import isConsentResolved from '../isConsentResolved'
import waitForConsent from '../waitForConsent'

export const LAZY_LOAD_FAILURE_EVENT = 'Lazy load failure message shown'

const ESSENTIAL_EVENTS = new Set([LAZY_LOAD_FAILURE_EVENT])
const HOLD_MS = 10000
const URL = 'v1/events'


// Emits an analytics event through the backend, which owns both the choice of
// analytics provider and the resolution of the caller's identity. Callers are
// user interactions, so this is deliberately fire-and-forget: it never blocks
// and never rejects.
//
// `application` is attached here rather than by the callers because the backend
// cannot work out which of our apps a request came from, and a caller that
// forgets it produces an event that nothing complains about.
//
export default function makeTrackEvent(application) {
  const holdUntil = Date.now() + HOLD_MS
  const heldEvents = []

  const isHolding = () => ( Date.now() < holdUntil )

  const send = ({ eventName, properties }) => {
    Client.post(URL, {
      event_name: eventName,
      properties: {
        ...properties,
        application,
      },
    }).catch((error) => {
      Sentry.captureException(error, { level: 'warning' })
    })
  }

  const sendIfConsented = (event) => {
    if ( hasAnalyticsConsent() ) {
      send(event)
    }
  }

  waitForConsent({
    handleResolved: () => {
      const events = heldEvents.splice(0)

      if ( isHolding() ) {
        events.forEach(sendIfConsented)
      }
    },
    timeoutMS: HOLD_MS,
  })

  return function trackEvent(eventName, properties) {
    const event = { eventName, properties }

    if ( ESSENTIAL_EVENTS.has(eventName) ) {
      send(event)
      return
    }

    if ( isConsentResolved() ) {
      sendIfConsented(event)
      return
    }

    if ( isHolding() ) {
      heldEvents.push(event)
    }
  }
}
